import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ModernNotification from './ModernNotification';

// Gerenciador global de notificações
class NotificationService {
  constructor() {
    this.notifications = [];
    this.listeners = [];
    this.nextId = 1;
  }

  // Adicionar listener para atualizações
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Notificar todos os listeners
  notify() {
    this.listeners.forEach(listener => listener(this.notifications));
  }

  // Adicionar notificação
  add(notification) {
    const id = this.nextId++;
    const newNotification = {
      id,
      timestamp: Date.now(),
      ...notification
    };

    this.notifications = [newNotification, ...this.notifications];
    
    // Limitar a 5 notificações simultâneas
    if (this.notifications.length > 5) {
      this.notifications = this.notifications.slice(0, 5);
    }

    this.notify();
    return id;
  }

  // Remover notificação
  remove(id) {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notify();
  }

  // Limpar todas
  clear() {
    this.notifications = [];
    this.notify();
  }

  // Métodos de conveniência
  success(message, options = {}) {
    return this.add({
      type: 'success',
      title: options.title || 'Sucesso!',
      message,
      duration: options.duration || 5000,
      ...options
    });
  }

  error(message, options = {}) {
    return this.add({
      type: 'error',
      title: options.title || 'Erro!',
      message,
      duration: options.duration || 7000,
      ...options
    });
  }

  warning(message, options = {}) {
    return this.add({
      type: 'warning',
      title: options.title || 'Atenção!',
      message,
      duration: options.duration || 6000,
      ...options
    });
  }

  info(message, options = {}) {
    return this.add({
      type: 'info',
      title: options.title || 'Informação',
      message,
      duration: options.duration || 4000,
      ...options
    });
  }

  // Método especial para mensagens importantes (preferências, etc.)
  important(message, options = {}) {
    return this.add({
      type: 'success',
      title: options.title || '✅ Operação Concluída',
      message,
      duration: options.duration || 6000,
      pauseOnHover: true,
      ...options
    });
  }
}

// Instância global
export const notificationService = new NotificationService();

// Hook para usar notificações em componentes React
export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);

  React.useEffect(() => {
    const unsubscribe = notificationService.subscribe(setNotifications);
    return unsubscribe;
  }, []);

  const removeNotification = useCallback((id) => {
    notificationService.remove(id);
  }, []);

  return {
    notifications,
    removeNotification
  };
};

// Componente container das notificações
export const NotificationContainer = () => {
  const { notifications, removeNotification } = useNotifications();

  // Renderizar no body usando portal
  return createPortal(
    <div style={{ 
      position: 'fixed', 
      bottom: 20, 
      right: 20, 
      zIndex: 10000,
      pointerEvents: 'none'
    }}>
      {notifications.map(notification => (
        <ModernNotification
          key={notification.id}
          {...notification}
          onClose={removeNotification}
        />
      ))}
    </div>,
    document.body
  );
};

export default NotificationContainer;
